/**
 * Ovitki — skupna čista logika za odjemalca IN Worker (T4 §6).
 *
 * ⚠️ **Ta modul obstaja, ker se je podvojena izvedba že razšla.** `trackKey()`
 * na odjemalcu in `artKey()` v Workerju sta bila napisana ločeno in razlikovala
 * sta se za en vezaj. Nič ni odpovedalo — oba niza ključev sta delovala — le
 * predpomnilnik bi se tiho razpolovil. Tega ne bi opazil nihče.
 *
 * `tsconfig.worker.json` ga zato izrecno vključi. To je edina datoteka iz
 * `src/`, ki jo Worker sme uvoziti, in razlog je natanko ta: dve izvedbi istega
 * pravila sta dražji od ene deljene.
 */

/**
 * Ključ predpomnilnika in podlaga za primerjavo.
 *
 * Male črke, brez diakritike, brez ločil, strnjeni presledki. Ločila odpadejo
 * v celoti — tudi vezaj med izvajalcem in naslovom — sicer bi bil ključ
 * odvisen od tega, kako ga je sestavil klicatelj.
 */
export function artKey(query: string): string {
  return query
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Kandidat vira, preden mu verjamemo.
 *
 * Vsi viri **ugibajo**: iščejo po prostem besedilu in vedno vrnejo najboljši
 * približek, ne glede na to, kako slab je. Zato URL sam po sebi ni odgovor —
 * spremljati ga morata naslov in izvajalec, da se da preveriti, ali gre res za
 * skladbo, ki smo jo iskali.
 */
export interface ArtCandidate {
  url: string;
  title?: string;
  artist?: string;
}

function tokens(value: string): string[] {
  return artKey(value)
    .split(' ')
    .filter((token) => token !== '');
}

/**
 * Ali kandidat res pripada poizvedbi.
 *
 * 🔴 **Brez tega vir vrne samozavesten nesmisel.** Izmerjeno na MusicBrainzu
 * 2026-07-20: poizvedba »zzz qqq xyz nesmisel« vrne posnetek »QQQ« izvajalca
 * »morihiro« z **oceno 100**. Ocena je relativna glede na poizvedbo in ne pove
 * ničesar o tem, ali je zadetek pravi; prag po oceni torej ne pomaga.
 *
 * Postaje ne pošiljajo samo skladb — pošiljajo reklame, jingle, ime programa
 * in ime voditelja. Vsak tak niz bi sicer dobil naključen album, prikazan kot
 * da je pravi.
 *
 * Pravilo:
 *   1. **vse** besede naslova morajo biti v poizvedbi, in
 *   2. če je poizvedba daljša od naslova — torej najbrž nosi še izvajalca —
 *      morajo biti v njej tudi besede izvajalca.
 *
 * Druga točka je tista, ki nesmisel ustavi: »diagnostika ena« vsebuje »ena«,
 * ne vsebuje pa »robert«.
 */
export function matchesQuery(query: string, candidate: ArtCandidate): boolean {
  const asked = new Set(tokens(query));
  if (asked.size === 0) return false;

  const title = new Set(tokens(candidate.title ?? ''));
  if (title.size === 0) return false;
  for (const token of title) {
    if (!asked.has(token)) return false;
  }

  // Poizvedba je sam naslov: izvajalca ni s čim primerjati in ga ne zahtevamo.
  //
  // ⚠️ Primerjata se MNOŽICI, ne dolžini. Naslov »Ena Ena« ima dve besedi in
  // eno samo različno; s štetjem ponovitev bi se izenačil z dvobesedno
  // poizvedbo »diagnostika ena« in nesmisel bi šel skozi. Test to čuva.
  if (title.size === asked.size) return true;

  const artist = tokens(candidate.artist ?? '');
  if (artist.length === 0) return false;
  return artist.some((token) => asked.has(token));
}

/**
 * Trik za visoko ločljivost (T3 §2).
 *
 * `artworkUrl100` se konča na `100x100bb.jpg` in je pripona prepisljiva.
 * Preverjeno: `1000x1000bb.jpg` = 92 KB, `2000x2000bb.jpg` = 168 KB, oba 200.
 * Brezplačno, brez avtentikacije. Jemljemo 1000, ker je 2000 za 52px sličico
 * zapravljanje.
 */
export function upgradeItunesArtwork(url: string): string {
  return url.replace(/\/\d+x\d+bb\.(jpg|png)$/i, '/1000x1000bb.$1');
}

interface ItunesResult {
  results?: { artworkUrl100?: string; trackName?: string; artistName?: string }[];
}

export function parseItunes(json: unknown): ArtCandidate | null {
  const first = (json as ItunesResult | null)?.results?.[0];
  const artwork = first?.artworkUrl100;
  if (first === undefined || artwork === undefined || artwork === '') return null;

  return {
    url: upgradeItunesArtwork(artwork),
    ...(first.trackName === undefined ? {} : { title: first.trackName }),
    ...(first.artistName === undefined ? {} : { artist: first.artistName }),
  };
}

export function itunesSearchUrl(query: string): string {
  const url = new URL('https://itunes.apple.com/search');
  url.searchParams.set('term', query);
  url.searchParams.set('media', 'music');
  url.searchParams.set('entity', 'song');
  url.searchParams.set('limit', '1');
  return url.toString();
}
